/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ElementRef, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { NGF_TOOLTIP_CONTENT, NgfTooltipContent } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-content';
import { NgfTooltipContentComponent } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-content.component';
import { NGF_TOOLTIP_STYLES, NgfTooltipStyles } from '@ng-falcon/ngf-tooltip/ngf-tooltip-content/ngf-tooltip-styles';
import { asyncScheduler, EMPTY, fromEvent, merge, Subscription } from 'rxjs';
import { filter, map, observeOn } from 'rxjs/operators';

export interface NgfTooltipConfig {
  readonly vcr: ViewContainerRef;
  readonly content: () => NgfTooltipContent;
  readonly styles?: () => NgfTooltipStyles;
  readonly canOpen?: () => boolean;
}

export interface HiddenTooltip {
  readonly trigger: ElementRef<HTMLElement>;
  readonly config: NgfTooltipConfig;
}

export interface NgfTooltipCreateOpt {
  readonly showOnHover?: boolean;
  readonly showOnFocus?: boolean;
}

export interface NgfTooltipFlashOpt {
  readonly duration?: number;
}

/**
 * @internal
 */
@Injectable({
  providedIn: 'root'
})
export class NgfTooltipService {

  private static readonly DEFAULT_FLASH_DURATION_MS = 500;

  private active: { trigger: ElementRef<HTMLElement>, config: NgfTooltipConfig, overlayRef: OverlayRef } | null = null;
  private readonly hidden: HiddenTooltip[] = [];

  private readonly tooltips: Map<ElementRef<HTMLElement>, Subscription> = new Map<ElementRef<HTMLElement>, Subscription>();

  constructor(private overlay: Overlay,
              private overlayPosition: OverlayPositionBuilder) { }

  create(trigger: ElementRef<HTMLElement>, config: NgfTooltipConfig, opt?: NgfTooltipCreateOpt): void {
    if (this.tooltips.has(trigger)) {
      // tooltip is already registered
      return;
    }

    const showOnHover = opt?.showOnHover ?? true;
    const showOnFocus = opt?.showOnFocus ?? true;

    const onHover$ = showOnHover
      ? merge(
        fromEvent(trigger.nativeElement, 'mouseenter').pipe(map(() => true)),
        fromEvent(trigger.nativeElement, 'mouseleave').pipe(map(() => false))
      ) : EMPTY;

    const onFocus$ = showOnFocus
      ? merge(
        fromEvent(trigger.nativeElement, 'focus').pipe(
          filter((event) => {
            const target = event.target;
            return target instanceof Element && target.matches(':focus-visible');
          }),
          map(() => true),
          observeOn(asyncScheduler) // required for proper change detection when focus is given programmatically
        ),
        fromEvent(trigger.nativeElement, 'blur').pipe(map(() => false))
      ) : EMPTY;

    const sub = merge(onHover$, onFocus$).subscribe(open => {
      if (open) {
        this.open(trigger, config);
      } else {
        this.close(trigger);
      }
    });

    this.tooltips.set(trigger, sub);
  }

  destroy(trigger: ElementRef<HTMLElement>): void {
    if (!this.tooltips.has(trigger)) {
      // tooltip is not registered
      return;
    }

    this.close(trigger);

    this.tooltips.get(trigger).unsubscribe();
    this.tooltips.delete(trigger);
  }

  flash(trigger: ElementRef<HTMLElement>, config: NgfTooltipConfig, opt?: NgfTooltipFlashOpt): void {
    this.open(trigger, config);
    setTimeout(() => this.close(trigger), opt?.duration ?? NgfTooltipService.DEFAULT_FLASH_DURATION_MS);
  }

  private open(trigger: ElementRef<HTMLElement>, config: NgfTooltipConfig): void {
    if (this.active?.trigger === trigger) {
      // already opened
      return;
    }

    if (config.canOpen && !config.canOpen()) {
      return;
    }

    if (this.active !== null) {
      this.hideActive();
    }

    const portal = new ComponentPortal(NgfTooltipContentComponent, config.vcr, Injector.create({
      providers: [
        { provide: NGF_TOOLTIP_CONTENT, useValue: config.content() },
        { provide: NGF_TOOLTIP_STYLES, useValue: config.styles ? config.styles() : null }
      ]
    }));

    const overlayRef = this.createOverlay(trigger);
    const ref = overlayRef.attach(portal);
    ref.changeDetectorRef.markForCheck();

    this.active = { trigger, config, overlayRef: overlayRef };
  }

  private close(trigger: ElementRef): void {
    if (this.active?.trigger === trigger) {
      this.closeActive();
    } else {
      this.closeHidden(trigger);
    }
  }

  private createOverlay(trigger: ElementRef<HTMLElement>): OverlayRef {
    return this.overlay.create({
      positionStrategy: this.overlayPosition
        .flexibleConnectedTo(trigger)
        .withTransformOriginOn('ngf-tooltip-content')
        .withPositions([
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            panelClass: 'ngf-tooltip-content--top'
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            panelClass: 'ngf-tooltip-content--bottom'
          },
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            panelClass: 'ngf-tooltip-content--left'
          },
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            panelClass: 'ngf-tooltip-content--right'
          },
        ]),
      scrollStrategy: this.overlay.scrollStrategies.close(),
      disposeOnNavigation: true,
    });
  }

  private hideActive(): void {
    this.hidden.push({
      trigger: this.active.trigger,
      config: this.active.config
    });
    this.active.overlayRef.dispose();
  }

  private closeActive(): void {
    this.active.overlayRef.dispose();
    this.active = null;

    if (this.hidden.length !== 0) {
      const { trigger, config } = this.hidden.pop();
      this.open(trigger, config);
    }
  }

  private closeHidden(trigger: ElementRef): void {
    const idx = this.hidden.findIndex(it => it.trigger === trigger);

    if (idx < 0) {
      return;
    }

    this.hidden.splice(idx, 1);
  }
}
